import React from 'react';
import styled from 'styled-components';
import Section, { SectionTitle } from '../../common/Section.js';
import Button from '../../common/Button.js';
import { Link } from 'react-router-dom';

const GenerationGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const GenerationItem = styled.div`
  text-align: center;
  
  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: var(--border-radius-md);
    margin-bottom: 1rem;
    box-shadow: var(--shadow-md);
  }
  
  h4 {
    color: var(--primary);
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutImage = styled.div`
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: var(--border-radius-md);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -20px;
    right: -20px;
    width: 70%;
    height: 70%;
    background-color: var(--secondary);
    z-index: -1;
    border-radius: var(--border-radius-md);
  }
`;

const AboutText = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  
  p {
    margin-bottom: 1.5rem;
  }
  
  .stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
    margin: 2rem 0;
    
    .stat {
      text-align: center;
      
      .number {
        font-size: 2.5rem;
        font-weight: 700;
        color: var(--secondary);
        margin-bottom: 0.5rem;
      }
      
      .label {
        font-size: 0.875rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 1px;
      }
    }
  }
`;

const AboutSection = () => {
  return (
    <Section>
      <div className="container">
        <SectionTitle>
          <h2>Shoffner Homes: A Legacy Built by Hand, Across Generations</h2>
          <p>Building in the NC Triad Since 1955 — Three Generations of Craftsmanship, Pride, and Purpose</p>
        </SectionTitle>
        
        <AboutContent>
          <div>
            <h3 style={{ marginBottom: '1.5rem', fontSize: '1.75rem', color: 'var(--primary)', fontWeight: 'bold', textAlign: 'center' }}>
              Shoffner Homes Construction
            </h3>
            
            <GenerationGallery>
              <GenerationItem>
                <h4>The Groundbreakers</h4>
                <img src="/images/sh1.png" alt="Generation One And Two" />
                <h4>Generation One & Two — My Grandfather and Young Father Digging Foundations in 1955</h4>
              </GenerationItem>
              
              <GenerationItem>
                <h4>The Builders</h4>
                <img src="/images/sh2.png" alt="Generation Two" />
                <h4>Generation One — My Grandfather Leading the Early Builds</h4>
              </GenerationItem>
            </GenerationGallery>
            
            <GenerationGallery>
              <GenerationItem>
                <h4>The Legacy</h4>
                <img src="/images/sh3.png" alt="Generation Three" />
                <h4>Generation Two — My Father Carrying the Shoffner Standard</h4>
              </GenerationItem>
              
              <GenerationItem>
                <h4>The Future</h4>
                <img src="/images/sh4.png" alt="Generation Four" />
                <h4>Generation Three — Building Forward with Purpose</h4>
              </GenerationItem>
            </GenerationGallery>
          </div>
          
          <AboutText>
          <p>
    Shoffner Homes began in <strong>1955</strong>, when my grandfather and father — then just a young man — worked 
    side-by-side digging foundations by hand in the red clay of the North Carolina Triad. That single act of breaking 
    ground, captured in the first image, wasn’t just the start of a home — it was the beginning of a three-generation legacy.
  </p>

  <p>
    My grandfather, a true craftsman and visionary, led the early builds with grit, skill, and a belief that homes 
    should be built to last. My father followed closely behind, learning not just how to build, but how 
    to lead — eventually taking the reins and growing Shoffner Homes into a trusted name known for 
    quality and integrity. That’s the legacy you see in the second and third images — boots on the ground, tools 
    in hand, building not just structures but standards.
  </p>

  <p>
    Today, as the third generation, I carry that responsibility forward. From overseeing custom homes to developing 
    entire communities, I build with the same pride and precision passed down to me. The photo of me on-site 
    represents more than a continuation — it’s a commitment to evolve, innovate, and honor the values my family laid down decades ago.
  </p>

  <p>
    From our humble beginnings to the homes we build today, Shoffner Homes stands for craftsmanship, tradition, 
    and trust. Every home reflects a history of hard work and a promise to deliver excellence — not just for today’s 
    families, but for generations to come.
  </p>

            
            
            <div className="stats">
              <div className="stat">
                <div className="number">150+</div>
                <div className="label">Homes Built</div>
              </div>
              <div className="stat" id="legacy-stat">
                <div className="number">Since 1955</div>
                <div className="label">Family-Owned Legacy</div>
              </div>
              <div className="stat">
                <div className="number">98%</div>
                <div className="label">Client Satisfaction</div>
              </div>
            </div>
            
          </AboutText>
        </AboutContent>
        
        {/* Move button outside the grid for easier centering */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginTop: '2rem',
          position: 'relative'
        }}>
          <Button 
           // as={Link} 
            to="/contact" 
            variant="secondary"
            style={{ 
              position: 'relative', 
              left: '8%', /* Adjust this value to align with the center stat */
              marginTop: '1.5rem' 
            }}
          >
            Learn More About Us
          </Button>
        </div>
      </div>
    </Section>
  );
};

export default AboutSection;